import { api } from './client'

/**
 * Alçada do gestor (spec 2026-07-29).
 *
 * Ser gestor concede, hoje, várias capacidades sobre a Organização de uma vez. Estas
 * rotas permitem ao Admin **tirar** uma delas de um par `(gestor, organização)`.
 *
 * Modelo de **subtração**: sem linha, o gestor pode — o comportamento é o de hoje.
 * Por isso a ausência de restrição é o estado normal, e a tela mostra "Concedida"
 * como padrão em vez de exigir que alguém marque algo.
 *
 * Só `admin:platform` cria ou remove. O gestor consegue **ler as suas** — é o que
 * permite à tela de Mensagens explicar o bloqueio em vez de exibir um botão que só
 * sabe responder 403.
 */

/** Capacidade restringível, vinda do registry em código do backend. */
export interface OrgCapability {
  key: string
  label: string
  description: string
}

/** Uma restrição de um gestor (o `reason` é obrigatório na origem). */
export interface ManagerRestriction {
  capability: string
  reason: string
  created_at: string
}

/** Restrição na visão por Organização — traz quem é o gestor. */
export interface OrgRestrictionRow extends ManagerRestriction {
  user_id: number
  user_email: string
  user_name: string | null
}

/** O registry: rótulos e descrições para montar as opções da tela. */
export async function listOrgCapabilities(): Promise<OrgCapability[]> {
  const res = await api.get<{ items: OrgCapability[] }>('/v1/org-capabilities')
  return res.data.items
}

/** Todas as restrições da Organização (admin). Evita uma requisição por gestor. */
export async function listOrgRestrictions(orgId: number): Promise<OrgRestrictionRow[]> {
  const res = await api.get<{ items: OrgRestrictionRow[] }>(
    `/v1/organizations/${orgId}/restrictions`,
  )
  return res.data.items
}

/** Restrições de um gestor. Admin, ou o próprio gestor consultando as suas. */
export async function listManagerRestrictions(
  orgId: number,
  userId: number,
): Promise<ManagerRestriction[]> {
  const res = await api.get<{ items: ManagerRestriction[] }>(
    `/v1/organizations/${orgId}/managers/${userId}/restrictions`,
  )
  return res.data.items
}

/** Tira a capacidade. `reason` é obrigatório — 400 `reason_required` se vazio. */
export async function addManagerRestriction(
  orgId: number,
  userId: number,
  capability: string,
  reason: string,
): Promise<void> {
  await api.post(`/v1/organizations/${orgId}/managers/${userId}/restrictions`, {
    capability,
    reason,
  })
}

/** Devolve a capacidade ao gestor. */
export async function removeManagerRestriction(
  orgId: number,
  userId: number,
  capability: string,
): Promise<void> {
  await api.delete(
    `/v1/organizations/${orgId}/managers/${userId}/restrictions/${capability}`,
  )
}

/** Mensagens PT-BR dos códigos de erro das rotas de alçada. */
export const RESTRICTION_ERROR_LABEL: Record<string, string> = {
  unknown_capability: 'Esta capacidade não existe no catálogo do sistema.',
  reason_required: 'Informe o motivo — ele fica registrado na trilha de auditoria.',
  restriction_exists: 'Esta capacidade já estava retirada deste gestor.',
  restriction_not_found: 'Esta capacidade não estava retirada.',
  permission_denied: 'Apenas administradores da plataforma alteram a alçada.',
  capability_restricted: 'Esta capacidade foi retirada de você nesta Organização.',
}
